import { useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import SubtleBadge from '@/components/common/SubtleBadge';
import {
  navigateToIndexWithPrefill,
  navigateToInspectWithPrefill,
} from '../lib/url-list-prefill';
import type { UrlStatusChangeKind, UrlStatusChangeRow } from '../api/types';
import { verdictLabel, type InspectCoverageBucket } from '../lib/inspect-detail';
import { IndexerIcon } from './IndexerIcon';
import {
  bucketStatusLabel,
  inspectJobChangeLabel,
  inspectJobChangeVariant,
} from '../lib/inspect-job-diff';
import { inspectDetailToSnapshot } from '../lib/seo-ai-prompt';
import { useIndexerPaths } from '../lib/indexer-paths';
import { copyText, decodeUrlForDisplay, formatTime } from '../lib/utils';
import { ActionTip } from './ActionTip';
import { CoverageHelpTip } from './CoverageHelpTip';
import { SearchConsoleIcon } from './SearchConsoleIcon';
import { SeoAiAdviceAction } from './SeoAiAdvice';
import { UrlActivityModal } from './UrlActivityModal';
import { InspectScheduleNotifyAction } from './InspectScheduleNotifyAction';

function changeRowToSnapshot(row: UrlStatusChangeRow) {
  return inspectDetailToSnapshot({
    url: row.url,
    success: row.afterBucket !== 'error',
    bucket: row.afterBucket as InspectCoverageBucket,
    verdict: row.afterVerdict ?? undefined,
    coverageState: row.afterCoverage ?? undefined,
    inspectLink: row.inspectLink ?? undefined,
    crawlVsLastmod: 'unknown',
    referringUrls: [],
    sitemaps: [],
  });
}

function bucketSubtleTone(
  bucket: UrlStatusChangeRow['beforeBucket'],
): 'success' | 'warning' | 'danger' | 'secondary' {
  if (!bucket) return 'secondary';
  if (bucket === 'indexed') return 'success';
  if (bucket === 'error') return 'danger';
  return 'warning';
}

function changeToneClass(kind: UrlStatusChangeKind): string {
  const variant = inspectJobChangeVariant(kind);
  if (variant === 'success') return 'comparison-hero-card--success';
  if (variant === 'warning') return 'comparison-hero-card--warning';
  if (variant === 'danger') return 'comparison-hero-card--danger';
  if (variant === 'info') return 'comparison-hero-card--primary';
  return 'comparison-hero-card--primary';
}

export function UrlStatusChangeLog({
  projectId,
  items,
  filtered = false,
}: {
  projectId: string;
  items: UrlStatusChangeRow[];
  /** وقتی فیلتر/جستجو فعال است متن empty متفاوت است */
  filtered?: boolean;
}) {
  const navigate = useNavigate();
  const paths = useIndexerPaths();
  const [detailUrl, setDetailUrl] = useState<string | null>(null);

  if (items.length === 0) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center text-center px-3 py-5">
        <div
          className="d-inline-flex align-items-center justify-content-center rounded-circle bg-200 text-secondary mb-3 p-3"
          aria-hidden
        >
          <IndexerIcon name="journal-text" className="fs-1" />
        </div>
        <p className="fw-medium mb-1">
          {filtered
            ? 'با این فیلتر یا جستجو موردی پیدا نشد.'
            : 'هنوز تغییری در لاگ ثبت نشده.'}
        </p>
        <p className="text-secondary small mb-3 mx-auto" style={{ maxWidth: 420 }}>
          {filtered
            ? 'فیلتر را عوض کنید یا جستجو را پاک کنید.'
            : 'فقط وقتی نتیجهٔ بازرسی نسبت به قبل عوض شود اینجا یک ردیف ظاهر می‌شود. اولین بازرسی هر آدرس در این لاگ نیست.'}
        </p>
        {!filtered && (
          <Link
            to={paths.urlInspection}
            className="btn btn-sm btn-outline-primary fs--2"
          >
            رفتن به وضعیت ایندکس
          </Link>
        )}
      </div>
    );
  }

  return (
    <>
      <ol className="list-unstyled mb-0 d-flex flex-column gap-3">
        {items.map((row) => (
          <ChangeLogItem
            key={row.id}
            row={row}
            onDetail={() => setDetailUrl(row.url)}
            onRequestIndex={() =>
              navigateToIndexWithPrefill(navigate, [row.url], 'url-changes')
            }
            onInspect={() =>
              navigateToInspectWithPrefill(navigate, [row.url], 'url-changes')
            }
          />
        ))}
      </ol>

      {detailUrl && (
        <UrlActivityModal
          projectId={projectId}
          url={detailUrl}
          show
          onHide={() => setDetailUrl(null)}
        />
      )}
    </>
  );
}

function ChangeLogItem({
  row,
  onDetail,
  onRequestIndex,
  onInspect,
}: {
  row: UrlStatusChangeRow;
  onDetail: () => void;
  onRequestIndex: () => void;
  onInspect: () => void;
}) {
  const decoded = decodeUrlForDisplay(row.url);
  const snapshot = changeRowToSnapshot(row);

  return (
    <li>
      <Card
        className={classNames(
          'mb-0 comparison-hero-card',
          changeToneClass(row.changeKind),
        )}
      >
        <Card.Body className="d-flex flex-column flex-sm-row gap-2 gap-sm-3 py-3">
          <time
            className="text-secondary fs--1 text-nowrap flex-shrink-0 pt-1"
            dateTime={row.createdAt}
            title={row.createdAt}
          >
            {formatTime(row.createdAt)}
          </time>

          <div className="flex-grow-1 min-w-0">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <SubtleBadge
                bg={inspectJobChangeVariant(row.changeKind)}
                pill
                className="fs--2"
              >
                {inspectJobChangeLabel(row.changeKind)}
              </SubtleBadge>
              {row.jobId && (
                <span className="text-secondary fs--2 mono" title={row.jobId}>
                  جاب {row.jobId.slice(0, 8)}…
                </span>
              )}
            </div>

            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-decoration-none mono if-url-clip text-start d-block mb-2"
              title={decoded}
              onClick={onDetail}
            >
              {decoded}
            </button>

            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <SubtleBadge
                bg={bucketSubtleTone(row.beforeBucket)}
                pill
                className="fs--2"
              >
                {bucketStatusLabel(row.beforeBucket)}
              </SubtleBadge>
              <span className="text-secondary" aria-hidden>
                ←
              </span>
              <SubtleBadge
                bg={bucketSubtleTone(row.afterBucket)}
                pill
                className="fs--2"
              >
                {bucketStatusLabel(row.afterBucket)}
              </SubtleBadge>
            </div>

            {(row.beforeCoverage || row.afterCoverage) && (
              <div className="d-flex flex-column gap-1 fs--1 mb-2">
                {row.beforeCoverage && (
                  <span className="text-truncate" title={row.beforeCoverage}>
                    <span className="text-secondary">قبلاً:</span>{' '}
                    {row.beforeCoverage}
                  </span>
                )}
                {row.afterCoverage && (
                  <span className="if-table-detail-with-help text-truncate">
                    <span className="text-secondary">اکنون:</span>{' '}
                    <span title={row.afterCoverage}>{row.afterCoverage}</span>
                    <CoverageHelpTip coverageState={row.afterCoverage} />
                  </span>
                )}
              </div>
            )}

            {row.afterVerdict && (
              <div className="text-secondary small mb-2">
                verdict: {verdictLabel(row.afterVerdict)}
              </div>
            )}

            <div className="d-inline-flex flex-nowrap align-items-center">
              <ActionTip tip="جزئیات + تاریخچه">
                <Button
                  size="sm"
                  variant="link"
                  className="if-table-action"
                  aria-label="جزئیات + تاریخچه"
                  onClick={onDetail}
                >
                  <IndexerIcon name="clock-history" />
                </Button>
              </ActionTip>
              <SeoAiAdviceAction
                url={row.url}
                snapshots={[snapshot]}
                disabled={row.afterBucket === 'indexed'}
              />
              {row.inspectLink && (
                <ActionTip tip="باز کردن در Search Console">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="if-table-action if-table-action--brand"
                    aria-label="باز کردن در Search Console"
                    href={row.inspectLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <SearchConsoleIcon />
                  </Button>
                </ActionTip>
              )}
              <ActionTip tip="درخواست ایندکس">
                <Button
                  size="sm"
                  variant="link"
                  className="if-table-action"
                  aria-label="درخواست ایندکس"
                  onClick={onRequestIndex}
                >
                  <IndexerIcon name="card-text" />
                </Button>
              </ActionTip>
              <ActionTip tip="بازرسی مجدد">
                <Button
                  size="sm"
                  variant="link"
                  className="if-table-action"
                  aria-label="بازرسی مجدد"
                  onClick={onInspect}
                >
                  <IndexerIcon name="search" />
                </Button>
              </ActionTip>
              <InspectScheduleNotifyAction url={row.url} source="url-changes" />
              <ActionTip tip="کپی آدرس">
                <Button
                  size="sm"
                  variant="link"
                  className="if-table-action"
                  aria-label="کپی آدرس"
                  onClick={() => void copyText(decoded)}
                >
                  <IndexerIcon name="copy" />
                </Button>
              </ActionTip>
            </div>
          </div>
        </Card.Body>
      </Card>
    </li>
  );
}
